import { MediaItem } from "@/lib/media/shared";

export interface ChatsState {
  chats: StoredChat[];
  last_cursor: Record<string, any> | null;
  has_more_pages: boolean;
  total_unread_count: number;
}

export const EMPTY_STATE = <ChatsState>{
  chats: [],
  last_cursor: {},
  has_more_pages: true,
  total_unread_count: 0,
};

export interface ResponseChat {
  user_id: string;
  receiver_id: string;
  conversation_id: string;
  created_at: number;
  is_read: boolean;
  last_message: MessagePreview;
  last_notification_timestamp: number;
  last_updated_at: number;
  sent_notification: boolean;
  unread_count: number;
  receiver_photo_url: string;
  receiver_complete_name: string;
  receiver_first_name: string;
  messages: Message[];
  assistants: Record<string, AssistantProfile> | null;
  receiver_response_time: number;
  is_ai_generated: boolean;
  is_unlocked: boolean;
}

export interface AssistantProfile {
  sub: string;
  name: string;
  personality: string;
  profile_photo: number;
  is_active: boolean;
}

export interface StoredChat {
  user_id: string;
  receiver_id: string;
  conversation_id: string;
  created_at: number;
  is_read: boolean;
  last_message: MessagePreview;
  last_notification_timestamp: number;
  last_updated_at: number;
  sent_notification: boolean;
  unread_count: number;
  receiver_photo_url: string;
  receiver_complete_name: string;
  receiver_first_name: string;
  last_cursor: Record<string, any> | null;
  has_more_pages: boolean;
  messages: Message[];
  is_assistant_typing?: boolean;
  assistants: Record<string, AssistantProfile> | null;
  receiver_response_time: number;
  is_receiver_active?: boolean;
  is_loading_messages?: boolean;
  is_ai_generated: boolean;
  is_unlocked: boolean;
}

export enum MessageType {
  TEXT = "text",
  LISTING = "listing",
  MEDIA = "media",
  ASSISTANT = "assistant",
  EVENT = "event",
}

interface BaseMessage<T extends MessageType, P> {
  conversation_id: string;
  message_id: string;
  is_deleted: boolean;
  receiver_id: string;
  sender_id: string;
  type: T;
  payload: P;
  timestamp: number;
}

export type Message =
  | BaseMessage<MessageType.TEXT, TextPayload>
  | BaseMessage<MessageType.LISTING, ListingPayload>
  | BaseMessage<MessageType.MEDIA, MediaPayload>
  | BaseMessage<MessageType.EVENT, EventPayload>
  | BaseMessage<MessageType.ASSISTANT, AssistantPayload>;

export interface MessagePreview {
  type: string;
  preview: string;
  timestamp: number;
  sender_id: string;
}

export interface TextPayload {
  text: string;
}

export interface ListingPayload {
  listings: MessageListingCard[];
  text?: string;
}

export interface EventPayload {
  event: {
    event_id: string;
    listing_id?: string;
    listing_title: string;
    slot_utc: string;
    status: string;
  };
  text?: string;
}

export type AssistantUIType =
  | "text"
  | "system"
  | "listing"
  | "listing_search"
  | "tours_list"
  | "tour_cancelled"
  | "tour_rescheduled"
  | "tours_selection"
  | "tour_confirmation"
  | "pick_slot"
  | "listing_selection"
  | "info"
  | "error";

export interface AssistantPayload {
  ui_type: AssistantUIType;
  data: Record<string, any>;
}

export interface ImageItem {
  url: string;
  width: number;
  height: number;
  size: number;
  mime: string;
}

export interface MediaDerivativeFile {
  Key: string;
  MimeType: string;
}

export interface MediaPayload {
  media: Record<string, Omit<MediaItem, "mediaId">>;
  text?: string;
}

export interface MessageListingCard {
  listing_id: string;
  title: string;
  price: number;
  photo: MediaItem;
  number_of_bathrooms: number;
  number_of_bedrooms: number;
  interior_space: number;
  lot_size: number;
}

export type ResolvedMedia = {
  url: string | null;
  type: "image" | "video" | "document" | "unknown";
  mime?: string;
  thumbnailUrl?: string | null;
};

export function getMediaArray(payload: MediaPayload): MediaItem[] {
  if (!payload?.media) return [];

  return Object.entries(payload.media).map(([mediaId, item]) => ({
    ...item,
    MediaId: mediaId,
  }));
}
